import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { ControlBase } from '../base/base.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'control-number',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzInputNumberModule, ReactiveFormsModule],
  template: `
    <nz-input-number
      [class]="baseClasses"
      [formControl]="control()"
      [nzPlaceHolder]="placeholder() || label()"
      [nzMin]="min() || DEFAULT_MIN"
      [nzMax]="max() || DEFAULT_MAX"
      (keydown)="onKeyDown($event)"
      (ngModelChange)="onInputChange($event)"
    >
      @if (suffix()) {
        <span nzInputSuffix>
          {{ suffix() }}
        </span>
      }
    </nz-input-number>
    @if (showMinMaxError() || invalidSeparator) {
      <div class="text-xs text-red-500 mt-1">
        {{ minMaxErrorMessage() }}
        @if(!invalidSeparator){
          <span>
            {{
              control().value < min()
                ? min()
                : control().value > max()
                  ? max()
                  : ''
            }}
          </span>
        }
      </div>
    }
  `,
})
export class ControlNumberComponent extends ControlBase {
  protected readonly DEFAULT_MIN = Number.MIN_SAFE_INTEGER;
  protected readonly DEFAULT_MAX = Number.MAX_SAFE_INTEGER;
  min = input<number>(this.DEFAULT_MIN);
  max = input<number>(this.DEFAULT_MAX);
  suffix = input<string>();
  invalidSeparator = false;

  private readonly transloco = inject(TranslocoService); // <-- Inject TranslocoService

  /**
   * Returns true if the control value is out of min/max bounds.
   */
  showMinMaxError(): boolean {
    const ctrl = this.control();
    if (!ctrl || ctrl.value === null || ctrl.value === undefined || ctrl.pristine) return false;
    const value = ctrl.value;
    return value < (this.min() ?? this.DEFAULT_MIN) || value > (this.max() ?? this.DEFAULT_MAX);
  }

  /**
   * Prevents entering a value greater than max.
   */
  onInputChange(value: number) {
    const max = this.max() ?? this.DEFAULT_MAX;
    const min = this.min() ?? this.DEFAULT_MIN;
    if (value > max) {
      this.control().setValue(max);
    } else if (value < min) {
      this.control().setValue(min);
    }
  }

  /**
   * Returns the appropriate error message for min/max violations or invalid separator.
   */
  minMaxErrorMessage(): string {
    if (this.invalidSeparator) {
      return this.transloco.translate('FILTERS.FORM.SEPARATOR');
    }
    const value = this.control()?.value;
    if (value < (this.min() ?? this.DEFAULT_MIN)) {
      return this.transloco.translate('FILTERS.FORM.MIN_VALUE', { value: this.min() });
    }
    if (value > (this.max() ?? this.DEFAULT_MAX)) {
      return this.transloco.translate('FILTERS.FORM.MAX_VALUE', { value: this.max() });
    }
    return '';
  }

  /**
   * Allows only numbers, control keys, and dot as decimal separator.
   * Prevents comma or other characters as decimal separator.
   * Shows feedback if comma is pressed.
   */
  onKeyDown(event: KeyboardEvent) {
    // Allow: backspace, delete, tab, escape, enter, arrows, home, end, etc.
    if (
      [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'Home', 'End'
      ].includes(event.key)
    ) {
      this.invalidSeparator = false;
      return;
    }
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())
    ) {
      this.invalidSeparator = false;
      return;
    }
    // Allow only one dot
    if (event.key === '.' || event.key === '-') {
      const input = event.target as HTMLInputElement;
      if (input.value.includes('.')) {
        event.preventDefault();
      }
      if (input.value.includes('-')) {
        event.preventDefault();
      }
      this.invalidSeparator = false;
      return;
    }
    // Prevent comma as decimal separator and show feedback
    if (event.key === ',') {
      event.preventDefault();
      this.invalidSeparator = true;
      return;
    }
    // Allow numbers only
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      this.invalidSeparator = false;
    } else {
      this.invalidSeparator = false;
    }
  }
}
